import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { onAnimationEnd } from '@theme/utilities';

class HeaderDrawer extends Component {
  requiredRefs = ['details'];

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('keyup', this.#onKeyUp);
    this.#setupAnimatedElementListeners();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keyup', this.#onKeyUp);
  }

  #onKeyUp = (event) => {
    if (event.key !== 'Escape') return;
    this.#close(this.#getDetailsElement(event));
  };

  get isOpen() {
    return this.refs.details.hasAttribute('open');
  }

  #getDetailsElement(event) {
    if (!(event?.target instanceof Element)) return this.refs.details;
    return event.target.closest('details') ?? this.refs.details;
  }

  #getSummary(details) {
    return details.querySelector('summary');
  }

  toggle() {
    return this.isOpen ? this.close() : this.open();
  }

  open(event) {
    const details = this.#getDetailsElement(event);
    const summary = this.#getSummary(details);
    if (!summary) return;

    summary.setAttribute('aria-expanded', 'true');
    this.preventInitialAccordionAnimations(details);
    this.#setupAnimatedElementListeners();

    requestAnimationFrame(() => {
      details.classList.add('menu-open');
      requestAnimationFrame(() => trapFocus(details));
    });
  }

  back(event) {
    this.#close(this.#getDetailsElement(event));
  }

  close() {
    this.#close(this.refs.details);
  }

  #close(details) {
    const summary = this.#getSummary(details);
    if (!summary) return;

    summary.setAttribute('aria-expanded', 'false');
    details.classList.remove('menu-open');

    onAnimationEnd(details, () => {
      reset(details);

      if (details === this.refs.details) {
        removeTrapFocus();
        this.querySelectorAll('details[open]:not(accordion-custom > details)').forEach(reset);
      } else {
        requestAnimationFrame(() => trapFocus(this.refs.details));
      }
    });
  }

  #setupAnimatedElementListeners() {
    function removeWillChangeOnAnimationEnd(event) {
      const target = event.target;
      if (target instanceof HTMLElement) {
        target.style.setProperty('will-change', 'unset');
        target.removeEventListener('animationend', removeWillChangeOnAnimationEnd);
      }
    }

    this.querySelectorAll('.menu-drawer__animated-element').forEach((element) => {
      element.addEventListener('animationend', removeWillChangeOnAnimationEnd);
    });
  }

  preventInitialAccordionAnimations(details) {
    const content = details.querySelectorAll('accordion-custom .details-content');

    content.forEach((el) => el.classList.add('details-content--no-animation'));

    setTimeout(() => {
      content.forEach((el) => el.classList.remove('details-content--no-animation'));
    }, 100);
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}

function reset(element) {
  element.classList.remove('menu-open');
  element.removeAttribute('open');
  element.querySelector('summary')?.setAttribute('aria-expanded', 'false');
}
